import React, { useState, useEffect, useCallback, Suspense, useMemo } from "react";
import { createPortal } from "react-dom";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import NightlightIcon from "@mui/icons-material/Nightlight";
import LightModeIcon from "@mui/icons-material/LightMode";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WarningIcon from "@mui/icons-material/Warning";
import PaymentIcon from "@mui/icons-material/Payment";
import { NavLink, Outlet, useNavigate, useLocation, matchPath } from "react-router-dom";
import getDecryptedToken from "../../api/getDecryptedToken";
import baseUrl from "../../api/baseUrl";
import ReminderModel from "../../components/reminder/ReminderModel";
import { Circles } from "react-loader-spinner";
import { useDispatch } from "react-redux";
import { io } from "socket.io-client";
import socket from "../../api/socket";
import { setNewPatients } from "../../redux/patients/patientsAction";
import {
  setMyStatusChange,
  setNewAllAppointment,
  setNewPurpose,
  setStatusChange,
} from "../../redux/appointment/appointmentAction";
import { setOnlineAppointment } from "../../redux/makanappointment/makeappoinmentAction";
import { isSubscriptionPaymentPath } from "../../utils/routes/filterDefaultRoutes";
import { getActiveClientRoutes } from "../../utils/routing/renderRoutes";
import { LAYOUT } from "../../pages/appointment/makeappointment/components/appointmentScheduleUi";
import { InvoicePrintTemplateProvider } from "../../context/InvoicePrintTemplateContext";
import InvoicePrintTemplateSelector from "../../components/invoice/InvoicePrintTemplateSelector";
import SmsNotificationButton from "../../components/sms/SmsNotificationButton";
import { useSmsAlert } from "../../hooks/useSmsAlert";
import { useChannelAlerts } from "../../hooks/useChannelAlerts";

const clinicName: string | undefined = getDecryptedToken("clinicName");
const userName: string | undefined = getDecryptedToken("userName");
const userRole: string | undefined = getDecryptedToken("role");

const DEFAULT_LOGO = "/logo.png?v=2";
const DEFAULT_LOGO_SMALL = "/logosmall.png?v=2";

function Layouts({ routes }: any) {
  const { alert: smsAlert } = useSmsAlert();
  const { alerts: channelAlerts } = useChannelAlerts();
  const [remainingStr, setRemainingStr] = useState<any>("");
  const [expiryDate, setExpiryDate] = useState<any>("");
  const [expired, setExpired] = useState(false);
  const [paymentDue, setPaymentDue] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [open, setopen] = useState(window.innerWidth > 768);
  const [theme, setTheme] = useState<"light" | "dark">("light");
  const [logout, setlogout] = useState(false);
  const [sidebarLogo, setSidebarLogo] = useState(DEFAULT_LOGO);
  const [sidebarLogoSmall, setSidebarLogoSmall] = useState(DEFAULT_LOGO_SMALL);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const visibleRoutes = useMemo(
    () => getActiveClientRoutes(routes ?? [], paymentDue),
    [routes, paymentDue]
  );

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    document.documentElement.setAttribute("data-theme", newTheme);
    document.documentElement.classList.toggle("dark", newTheme === "dark");
    localStorage.setItem("theme", newTheme);
  };

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme") as "light" | "dark";
    if (savedTheme) {
      setTheme(savedTheme);
      document.documentElement.setAttribute("data-theme", savedTheme);
      document.documentElement.classList.toggle("dark", savedTheme === "dark");
    }
  }, []);

  const handleSocketUpdate = useCallback(
    (event: any, action: Function) => {
      if (event.token.clinicId === getDecryptedToken("cid")) {
        event.data.new = true;
        dispatch(action(event.data));
      }
    },
    [dispatch]
  );

  const eid = getDecryptedToken("eid");

  useEffect(() => {
    socket.on("updatePatients", (e) => handleSocketUpdate(e, setNewPatients));
    socket.on("allAppointmentStatusChange", (e) =>
      handleSocketUpdate(e, setStatusChange)
    );
    socket.on("myAppointmentStatusChange", (e) => {
      if (eid === e.data.doctorId) {
        handleSocketUpdate(e, setMyStatusChange);
      }
    });
    socket.on("uppdateAppointment", (e) => {
      handleSocketUpdate(e, setNewAllAppointment);
    });
    socket.on("uppdateMyAppointment", (e) => { });
    socket.on("updatepurpose", (e) => handleSocketUpdate(e, setNewPurpose));

    return () => {
      socket.off("updatePatients");
      socket.off("uppdateAppointment");
      socket.off("uppdateMyAppointment");
      socket.off("updatepurpose");
    };
  }, [handleSocketUpdate, eid]);

  useEffect(() => {
    // Initialize Socket.IO connection for online appointments
    // Note: path must match the server configuration exactly
    const serverUrl = process.env.REACT_APP_WS || "http://192.168.8.119:5001";
    // Namespace is part of the URL; Engine.IO path stays the default /socket.io/
    const socketOptions = {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      withCredentials: true,
    };

    const onlineAppointmentsSocket = io(`${serverUrl}/online-appointments`, socketOptions);

    // Connection event handlers
    onlineAppointmentsSocket.on("connect", () => {
    });

    onlineAppointmentsSocket.on("disconnect", (reason) => {
    });

    onlineAppointmentsSocket.on("connect_error", (error: any) => {
      console.error("Online appointments socket connection error:", error);

    });

    onlineAppointmentsSocket.on("onlineAppointments", (appointment) => {
      dispatch(setOnlineAppointment(appointment));
    });

    // Cleanup on component unmount
    return () => {
      if (onlineAppointmentsSocket.connected) {
        onlineAppointmentsSocket.disconnect();
      }
    };
  }, [dispatch]);

  const handleClose = useCallback(() => {
    if (window.innerWidth <= 768) {
      setopen(false);
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setopen(true);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Update document title based on active route (handles both NEWROUTES and ALLROUTES)
  useEffect(() => {
    if (!routes || routes.length === 0) return;

    // Helper to capitalize title properly - Title Case
    const capitalizeTitle = (title: string): string => {
      if (!title) return title;
      // Convert to Title Case: first letter of each word capitalized, rest lowercase
      return title
        .split(" ")
        .map((word) => {
          if (!word) return word;
          // Handle words with special characters
          return word
            .split(/([^a-zA-Z])/)
            .map((part) => {
              if (!part || !/^[a-zA-Z]+$/.test(part)) return part;
              return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
            })
            .join("");
        })
        .join(" ");
    };

    // Recursively find the active route and collect title hierarchy
    const findActiveRouteWithHierarchy = (routeList: any[], currentPath: string, parentPath: string = "", titleHierarchy: any[] = []): { route: any; hierarchy: any[] } | null => {
      // Sort by path length (longest first) to match more specific routes first
      const sortedRoutes = [...routeList]
        .filter((route) => {
          const path = route.PATH || route.path;
          return path && !route.DISABLED && !route.disabled;
        })
        .sort((a, b) => {
          const pathA = a.PATH || a.path || "";
          const pathB = b.PATH || b.path || "";
          return pathB.length - pathA.length;
        });

      for (const route of sortedRoutes) {
        const routePath = route.PATH || route.path;
        if (!routePath) continue;

        // Build full path for matching (handle both relative and absolute paths)
        let fullPath = routePath;
        if (!routePath.startsWith("/") && parentPath) {
          // Relative path - combine with parent
          const cleanParent = parentPath.replace(/\/+$/, "");
          const cleanRoute = routePath.replace(/^\/+/, "");
          fullPath = `${cleanParent}/${cleanRoute}`;
        } else if (!routePath.startsWith("/")) {
          // Relative path without parent - might need base path
          // Check if currentPath starts with /client
          if (currentPath.startsWith("/client")) {
            fullPath = `/client/${routePath}`;
          } else {
            fullPath = `/${routePath}`;
          }
        }

        // Use React Router's matchPath for proper route matching
        const match = matchPath(
          {
            path: fullPath,
            end: false, // Allow partial matches for nested routes
          },
          currentPath
        );

        if (match !== null) {
          const currentHierarchy = [...titleHierarchy, route];

          // Check if there's a more specific match in subnav/SUBNAV
          const subnav = route.SUBNAV || route.subnav;
          if (subnav && subnav.length > 0) {
            const subResult = findActiveRouteWithHierarchy(subnav, currentPath, fullPath, currentHierarchy);
            if (subResult) {
              return subResult;
            }
          }
          return { route, hierarchy: currentHierarchy };
        }

        // Also check subnav/SUBNAV recursively
        const subnav = route.SUBNAV || route.subnav;
        if (subnav && subnav.length > 0) {
          const subResult = findActiveRouteWithHierarchy(subnav, currentPath, fullPath, titleHierarchy);
          if (subResult) {
            return subResult;
          }
        }
      }
      return null;
    };

    // Find active route with its hierarchy
    const routeResult = findActiveRouteWithHierarchy(routes, location.pathname);

    // Capitalize clinic name
    const formattedClinicName = clinicName ? capitalizeTitle(clinicName) : null;

    if (routeResult && routeResult.hierarchy.length > 0) {
      // Format: clinicName | TITLE - SUBNAV.TITLE - SUBNAV.TITLE
      const routeTitles = routeResult.hierarchy
        .map(route => {
          const title = route.TITLE || route.title;
          return title ? capitalizeTitle(title) : null;
        })
        .filter(Boolean)
        .join(" - ");

      document.title = formattedClinicName
        ? `${formattedClinicName} | ${routeTitles}`
        : routeTitles;
    } else if (routeResult && routeResult.route) {
      // Fallback: just use the active route title
      const title = routeResult.route.TITLE || routeResult.route.title;
      if (title) {
        const formattedTitle = capitalizeTitle(title);
        document.title = formattedClinicName
          ? `${formattedClinicName} | ${formattedTitle}`
          : formattedTitle;
      } else {
        document.title = formattedClinicName || "Dashboard";
      }
    } else {
      // If no route found, try to match by checking if pathname starts with known base paths
      const pathSegments = location.pathname.split("/").filter(Boolean);
      if (pathSegments.length > 0) {
        const lastSegment = pathSegments[pathSegments.length - 1];
        const routeBySegment = routes.find((route: any) => {
          const path = route.PATH || route.path || "";
          const normalizedPath = path.replace(/^\/+|\/+$/g, "").toLowerCase();
          return normalizedPath === lastSegment.toLowerCase() ||
            normalizedPath.includes(lastSegment.toLowerCase());
        });

        if (routeBySegment) {
          const title = routeBySegment.TITLE || routeBySegment.title;
          if (title) {
            const formattedTitle = capitalizeTitle(title);
            document.title = formattedClinicName
              ? `${formattedClinicName} | ${formattedTitle}`
              : formattedTitle;
          } else {
            document.title = formattedClinicName || "Dashboard";
          }
        } else {
          document.title = formattedClinicName || "Dashboard";
        }
      } else {
        document.title = formattedClinicName || "Dashboard";
      }
    }
  }, [location.pathname, routes]);

  const getRemaining = (targetDate: Date) => {
    const diff = targetDate.getTime() - Date.now();
    return diff > 0 ? diff : 0;
  };

  const updateRemainingStr = (ms: number) => {
    let secs = Math.floor(ms / 1000);
    const days = Math.floor(secs / 86_400);
    secs %= 86_400;
    const h = String(Math.floor(secs / 3600)).padStart(2, "0");
    const m = String(Math.floor((secs % 3600) / 60)).padStart(2, "0");
    const s = String(secs % 60).padStart(2, "0");
    setRemainingStr(`${days}d ${h}:${m}:${s}`);
  };

  const getData = useCallback(async () => {
    try {
      const response = await baseUrl.get(
        "client/payment/getCientClinicPaymentbyId"
      );
      if (response?.data?.success) {
        const isoString = response.data.data;
        const target = new Date(isoString);
        setExpiryDate(target);
        const remaining = getRemaining(target);
        const isDue = remaining === 0;
        setPaymentDue(isDue);
        updateRemainingStr(remaining);
      } else {
        setPaymentDue(false);
      }
    } catch (error: any) {
      console.error(error?.response || error.message);
    }
  }, []);

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    let cancelled = false;
    baseUrl
      .get("/clinic/getBillData")
      .then((res) => {
        if (cancelled || !res?.data?.success) return;
        const clinic = res.data.data;
        const hasLogo = clinic?.logo === true;
        const fullLogo =
          typeof clinic?.logoimage === "string" ? clinic.logoimage.trim() : "";
        const smallLogo =
          typeof clinic?.logoSmallimage === "string"
            ? clinic.logoSmallimage.trim()
            : "";

        if (hasLogo && fullLogo) {
          setSidebarLogo(fullLogo);
        } else {
          setSidebarLogo(DEFAULT_LOGO);
        }

        if (hasLogo && smallLogo) {
          setSidebarLogoSmall(smallLogo);
        } else if (hasLogo && fullLogo) {
          // Fall back to full clinic logo if small is missing
          setSidebarLogoSmall(fullLogo);
        } else {
          setSidebarLogoSmall(DEFAULT_LOGO_SMALL);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSidebarLogo(DEFAULT_LOGO);
          setSidebarLogoSmall(DEFAULT_LOGO_SMALL);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!paymentDue) return;
    if (!isSubscriptionPaymentPath(location.pathname)) {
      navigate("/client/pay", { replace: true });
    }
  }, [paymentDue, location.pathname, navigate]);

  /** index.scss sets overflow:hidden on body/#dental — that clips every fixed modal */
  useEffect(() => {
    const dental = document.getElementById("dental");
    const prevBody = document.body.style.overflow;
    const prevDental = dental?.style.overflow ?? "";
    document.body.style.overflow = "visible";
    if (dental) dental.style.overflow = "visible";
    return () => {
      document.body.style.overflow = prevBody;
      if (dental) dental.style.overflow = prevDental;
    };
  }, []);

  useEffect(() => {
    if (!expiryDate) return;
    const id = setInterval(() => {
      const ms = getRemaining(expiryDate);
      const isDue = ms === 0;
      setExpired(isDue);
      updateRemainingStr(ms);
      if (isDue) {
        setPaymentDue(true);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [expiryDate]);

  const closeSubscriptionModal = useCallback(() => {
    setShowSubscriptionModal(false);
  }, []);

  const hasSubscriptionData =
    expiryDate instanceof Date && !Number.isNaN(expiryDate.getTime());

  const logOut = () => {
    localStorage.clear();
    window.location.reload();
    window.location.href = "/login";
  };

  const isInvoicePage =
    !!matchPath({ path: "/client/payments/invoice/:ID", end: true }, location.pathname) ||
    !!matchPath({ path: "/client/payments/create-invoice", end: true }, location.pathname);

  return (
    <>
      {/* Shell scroll lives on .layout-outlet-scroll; body/#dental must not clip fixed modals */}
      <style>{`
        html:has(.layout-root-ios) {
          height: 100%;
          overflow: hidden;
        }
        html:has(.layout-root-ios) body,
        html:has(.layout-root-ios) #dental {
          height: 100%;
          overflow: visible !important;
        }
        .layout-root-ios {
          display: flex;
          flex-direction: column;
          min-height: 0;
        }
        .layout-content-column {
          display: flex;
          flex-direction: column;
          left: 0;
          min-height: 0;
          min-width: 0;
          width: 100%;
        }
        @media (min-width: 768px) {
          .layout-content-column[data-sidebar-open="true"] {
            left: 220px;
            width: calc(100% - 220px);
          }
          .layout-content-column[data-sidebar-open="false"] {
            left: 56px;
            width: calc(100% - 56px);
          }
        }
        @media (max-width: 768px) {
          .layout-root-ios {
            height: 100dvh !important;
            max-height: -webkit-fill-available;
          }
          .layout-outlet-scroll {
            overflow-y: scroll !important;
            -webkit-overflow-scrolling: touch;
            touch-action: pan-y;
            overscroll-behavior-y: contain;
          }
        }
      `}</style>
      <div className={LAYOUT.root}>
        {/* Sidebar - Fixed */}
        <aside
          className={`${LAYOUT.sidebar} flex flex-col ${open
              ? "w-[220px] translate-x-0"
              : "md:w-[56px] md:translate-x-0 w-[220px] -translate-x-full"
            }`}
          style={{
            transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            willChange: 'width, transform'
          }}
        >
          {/* Logo Section */}
          <div
            className={`${LAYOUT.sidebarBrand} shrink-0 ${open ? "h-20 px-4" : "md:h-16 md:justify-center md:px-2 h-20 px-4"}`}
            style={{
              transition: 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1), padding 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <div
              className="relative flex items-center overflow-hidden"
              style={{
                width: open ? '200px' : '30px',
                height: open ? '56px' : '30px',
                transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), height 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              <img
                src={sidebarLogo}
                alt={clinicName || "Clinic logo"}
                className={`w-full h-full object-contain object-left cursor-pointer transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0'
                  }`}
                onClick={() => navigate("/client/dashboard")}
                onError={(e) => {
                  const img = e.currentTarget;
                  if (img.src !== window.location.origin + DEFAULT_LOGO && !img.src.endsWith(DEFAULT_LOGO)) {
                    img.src = DEFAULT_LOGO;
                  }
                }}
              />
              <img
                src={sidebarLogoSmall}
                alt={clinicName || "Clinic logo"}
                className={`absolute object-contain cursor-pointer transition-opacity duration-300 ${open ? 'opacity-0' : 'opacity-100'
                  }`}
                style={{
                  width: '30px',
                  height: '30px',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)'
                }}
                onClick={() => navigate("/client/dashboard")}
                onError={(e) => {
                  const img = e.currentTarget;
                  if (img.src !== window.location.origin + DEFAULT_LOGO_SMALL && !img.src.endsWith(DEFAULT_LOGO_SMALL)) {
                    img.src = DEFAULT_LOGO_SMALL;
                  }
                }}
              />
            </div>
            <button
              onClick={() => setopen(false)}
              className={`${LAYOUT.iconBtn} text-slate-400 md:hidden flex-shrink-0`}
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 min-h-0 overflow-y-auto py-4">
            <div
              className={`px-3 ${!open ? "md:px-2" : ""}`}
              style={{
                transition: 'padding 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              <ul className="space-y-1">
                {visibleRoutes?.map((item: any, i: number) => (
                  <li key={i}>
                    <NavLink
                      to={item?.PATH}
                      onClick={handleClose}
                      className={({ isActive }) =>
                        `flex items-center py-2 rounded-xl cursor-pointer group focus:outline-none transition-all duration-200 ${open ? "" : "md:justify-center"
                        } ${isActive ? LAYOUT.navActive : LAYOUT.navIdle} [&_.icon-wrapper_svg_path]:fill-primary group-hover:[&_.icon-wrapper_svg_path]:fill-primary ${isActive ? "[&_.icon-wrapper_svg_path]:fill-white" : ""
                        }`}
                      style={{
                        gap: open ? "12px" : "0px",
                        paddingLeft: open ? "12px" : "8px",
                        paddingRight: open ? "12px" : "8px",
                        justifyContent: open ? "flex-start" : "center",
                        transition:
                          "gap 0.3s cubic-bezier(0.4, 0, 0.2, 1), padding 0.3s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s",
                      }}
                      title={!open ? item?.TITLE : undefined}
                    >
                      <div className="icon-wrapper w-6 h-6 rounded-lg flex items-center justify-center transition-colors [&_svg]:w-4 [&_svg]:h-4 flex-shrink-0">
                        {item?.ICON && <item.ICON className="text-slate-600 dark:text-slate-300" />}
                      </div>
                      <span
                        className={`text-xs font-medium capitalize whitespace-nowrap overflow-hidden inline-block ${open ? 'opacity-100' : 'md:opacity-0 opacity-100'
                          }`}
                        style={{
                          maxWidth: open ? '200px' : '0px',
                          width: open ? 'auto' : '0px',
                          transition: 'opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1) 0.05s, max-width 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          willChange: 'opacity, max-width, width'
                        }}
                      >
                        {item?.TITLE}
                      </span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          </nav>

          {/* Bottom Logo — Powered by */}
          <div
            className={`shrink-0 flex flex-col border-t border-slate-200/70 dark:border-slate-800 ${open ? "px-4 py-3 items-start" : "md:px-2 md:py-3 md:items-center px-4 py-3 items-start"}`}
            style={{
              transition: 'padding 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <span
              className={`text-[10px] font-medium uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1.5 whitespace-nowrap ${open ? "block" : "md:hidden"
                }`}
            >
              Powered by
            </span>
            <img
              src={open ? "/logo.png?v=2" : "/logosmall.png?v=2"}
              alt="Care32dentsoftware"
              className={`object-contain cursor-pointer ${open ? "w-[200px] h-[44px] object-left" : "w-[30px] h-[30px]"}`}
              onClick={() => navigate("/client/dashboard")}
            />
          </div>
        </aside>

        {/* Mobile Overlay */}
        <div
          onClick={() => setopen(false)}
          className={`${LAYOUT.mobileOverlay} ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
            }`}
        />

        {/* Main column: fixed from sidebar edge → right (avoids w-full + ml overflow) */}
        <div
          className="layout-content-column fixed inset-y-0 right-0 z-30 flex flex-col transition-[left,width] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]"
          data-sidebar-open={open ? "true" : "false"}
        >
          <InvoicePrintTemplateProvider>
          {/* Header — full width of content column */}
          <header className={`${LAYOUT.header} relative z-[100] w-full shrink-0 overflow-visible`}>
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-primary/80 via-primaryLight to-primary/30"
            />

            {/* Left Section */}
            <div className="flex min-w-0 items-center gap-3 h-full">
              <button
                onClick={() => setopen((e) => !e)}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200/80 bg-white text-slate-600 shadow-sm transition-all duration-200 hover:border-primary/30 hover:bg-primary/5 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-primary/40 dark:hover:bg-primary/10 dark:hover:text-primaryLight"
                aria-label="Toggle menu"
              >
                {open ? (
                  <MenuOpenIcon sx={{ fontSize: 18 }} />
                ) : (
                  <MenuIcon sx={{ fontSize: 18 }} />
                )}
              </button>
              <div className="hidden min-w-0 sm:block">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500 leading-none mb-1">
                  Clinic
                </p>
                <h1 className="truncate text-base font-semibold tracking-tight text-slate-800 dark:text-white leading-none mb-0">
                  {clinicName}
                </h1>
              </div>
            </div>

            {isInvoicePage && (
              <div className="flex-1 flex justify-center px-2 min-w-0">
                <InvoicePrintTemplateSelector />
              </div>
            )}

            {/* Right Section */}
            <div className="flex shrink-0 items-center gap-1.5 md:gap-2">
              <div className="flex items-center gap-0.5 rounded-2xl border border-slate-200/70 bg-slate-50/80 p-1 dark:border-slate-700/80 dark:bg-slate-800/50">
                {hasSubscriptionData && (
                  <button
                    type="button"
                    onClick={() => setShowSubscriptionModal(true)}
                    className={`${LAYOUT.iconBtn} relative`}
                    aria-label="Subscription time remaining"
                    title="Subscription status"
                  >
                    <AccessTimeIcon
                      sx={{ fontSize: 20 }}
                      className={expired ? "text-red-500" : "text-emerald-600 dark:text-emerald-400"}
                    />
                    {expired && (
                      <span className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-red-500 ring-2 ring-white dark:ring-slate-800" />
                    )}
                  </button>
                )}
                <button
                  onClick={toggleTheme}
                  className={LAYOUT.iconBtn}
                  aria-label="Toggle theme"
                >
                  {theme === "light" ? (
                    <NightlightIcon sx={{ fontSize: 20 }} />
                  ) : (
                    <LightModeIcon sx={{ fontSize: 20 }} className="text-amber-400" />
                  )}
                </button>
                <SmsNotificationButton alert={smsAlert} channelAlerts={channelAlerts} />
              </div>

              {/* User Menu */}
              <div className="relative">
                <button
                  onClick={() => setlogout(!logout)}
                  className={LAYOUT.userMenuBtn}
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primaryLight text-sm font-semibold text-white shadow-sm shadow-primary/25 ring-2 ring-white dark:ring-slate-800">
                    {userName?.charAt(0)?.toUpperCase() || "U"}
                  </div>
                  <div className="hidden min-w-0 md:block text-left">
                    <span className="block max-w-[110px] truncate text-sm font-semibold capitalize leading-tight text-slate-800 dark:text-slate-100">
                      {userName}
                    </span>
                    {userRole ? (
                      <span className="block max-w-[110px] truncate text-[10px] font-medium leading-tight text-slate-400 dark:text-slate-500">
                        {userRole}
                      </span>
                    ) : null}
                  </div>
                </button>

                {/* Dropdown */}
                {logout && (
                  <>
                    <div
                      className="fixed inset-0 z-[90]"
                      onClick={() => setlogout(false)}
                    />
                    <div className={`${LAYOUT.menuPanel} z-[110]`}>
                      <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700/80">
                        <p className="text-sm font-semibold truncate text-slate-800 dark:text-white">
                          {userName}
                        </p>
                        {userRole ? (
                          <p className="text-xs font-medium text-primary mt-0.5 truncate">
                            {userRole}
                          </p>
                        ) : null}
                      </div>
                      {userRole === "Super Admin" && (
                        <button
                          type="button"
                          onClick={() => {
                            setlogout(false);
                            navigate("/my-account");
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800 active:bg-slate-100 dark:active:bg-slate-700 focus:outline-none transition-colors"
                        >
                          <PersonOutlineIcon sx={{ fontSize: 18 }} />
                          My Account
                        </button>
                      )}
                      <button
                        onClick={logOut}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 active:bg-red-100 dark:active:bg-red-900/50 focus:outline-none transition-colors"
                      >
                        <LogoutIcon sx={{ fontSize: 18 }} />
                        Log Out
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </header>
          {/* Page scroll only on inner wrapper — keeps fixed overlays viewport-sized */}
          <main className={`${LAYOUT.main} flex flex-col min-h-0`}>
            <div className={`${LAYOUT.outletScroll} layout-outlet-scroll flex-1 min-h-0`}>
              <Suspense
                fallback={
                  <div className="flex items-center justify-center h-full">
                    <Circles
                      height="60"
                      width="60"
                      color="#174d7a"
                      ariaLabel="loading"
                    />
                  </div>
                }
              >
                <Outlet />
              </Suspense>
            </div>
          </main>
          </InvoicePrintTemplateProvider>
        </div>
        {showSubscriptionModal &&
          hasSubscriptionData &&
          createPortal(
            <div
              className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-md"
              onClick={closeSubscriptionModal}
            >
              <div
                className="w-full max-w-sm overflow-hidden rounded-2xl border border-slate-200/60 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between border-b border-slate-200/70 px-5 py-4 dark:border-slate-700">
                  <h2 className="flex items-center gap-2 text-base font-semibold text-slate-800 dark:text-white">
                    <AccessTimeIcon sx={{ fontSize: 22 }} className="text-primary" />
                    Subscription status
                  </h2>
                  <button
                    type="button"
                    onClick={closeSubscriptionModal}
                    className={LAYOUT.iconBtn}
                    aria-label="Close"
                  >
                    <CloseIcon sx={{ fontSize: 20 }} />
                  </button>
                </div>
                <div className="space-y-4 px-5 py-5">
                  {expired ? (
                    <div className="flex items-center gap-2 text-red-600">
                      <WarningIcon sx={{ fontSize: 22 }} />
                      <span className="text-sm font-semibold">Subscription expired</span>
                    </div>
                  ) : (
                    <div>
                      <p className="text-xs font-medium text-slate-500">Time remaining</p>
                      <p className="mt-1 font-mono text-2xl font-semibold tracking-tight text-slate-800 dark:text-white">
                        {remainingStr}
                      </p>
                    </div>
                  )}
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Subscribe or renew your monthly plan to keep using all features without interruption.
                  </p>
                </div>
                <div className="flex justify-end gap-2 border-t border-slate-200/70 px-5 py-4 dark:border-slate-700">
                  <button
                    type="button"
                    onClick={closeSubscriptionModal}
                    className={`${LAYOUT.iconBtn} px-4 text-sm font-medium`}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      closeSubscriptionModal();
                      navigate("/client/pay");
                    }}
                    className={`${LAYOUT.btnCompact} flex items-center gap-1`}
                  >
                    <PaymentIcon sx={{ fontSize: 16 }} />
                    Subscribe
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

        {paymentDue && !isSubscriptionPaymentPath(location.pathname) &&
          createPortal(<ReminderModel />, document.body)}
      </div>
    </>
  );
}

export default Layouts;
